import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Send, Upload, Mic, X, Play, StopCircle, Plus,
  Trash2, RefreshCw, AlertCircle, CheckCircle2,
  Clock, MessageSquare, Settings2,
  Image, Video, FileText, ArrowUp, ArrowDown, Type, Music,
  CalendarClock, Phone, User, ChevronDown,
} from 'lucide-react';
import api from '../../services/api';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const STATUS_CONFIG = {
  agendada: { label: 'Agendada', color: 'bg-blue-100 text-blue-700' },
  enviando: { label: 'Enviando', color: 'bg-yellow-100 text-yellow-700' },
  enviada: { label: 'Enviada', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-gray-100 text-gray-600' },
  erro: { label: 'Erro', color: 'bg-red-100 text-red-700' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.agendada;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

const STEP_LABELS = { texto: 'Texto', imagem: 'Imagem', video: 'Vídeo', audio: 'Áudio', documento: 'Documento' };
const STEP_ICONS = { texto: Type, imagem: Image, video: Video, audio: Music, documento: FileText };

const SHORTCODES = [
  { code: '#primeironome', desc: 'Primeiro nome do contato' },
  { code: '#nomecompleto', desc: 'Nome completo' },
  { code: '#periodododia', desc: 'Bom dia / Boa tarde / Boa noite' },
  { code: '#diasemana', desc: 'Ex: Segunda-feira' },
  { code: '#dia', desc: 'Dia do mês' },
  { code: '#mes', desc: 'Nome do mês' },
];

function AudioRecorder({ onAudioReady }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mr = useRef(null); const chunks = useRef([]); const timer = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunks.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => { const blob = new Blob(chunks.current, { type: 'audio/webm' }); onAudioReady({ blob, url: URL.createObjectURL(blob), nome: `audio_${Date.now()}.webm` }); stream.getTracks().forEach(t => t.stop()); };
      rec.start(); mr.current = rec; setRecording(true); setSeconds(0);
      timer.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch { alert('Microfone indisponível.'); }
  };
  const stop = () => { mr.current?.stop(); clearInterval(timer.current); setRecording(false); };
  return recording
    ? <button onClick={stop} className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"><StopCircle size={13} /> Parar ({seconds}s)</button>
    : <button onClick={start} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"><Mic size={13} /> Gravar PTT</button>;
}

function ShortcodeSelector({ onInsert }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium hover:bg-blue-100">
        <span>#</span> Atalhos <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-56">
          {SHORTCODES.map(s => (
            <button key={s.code} type="button" onClick={() => { onInsert(s.code); setOpen(false); }} className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center gap-2">
              <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{s.code}</span>
              <span className="text-xs text-gray-500">{s.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StepCard({ step, index, total, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const Icon = STEP_ICONS[step.tipo];
  const handleFile = (e) => { const file = e.target.files[0]; if (!file) return; onUpdate({ ...step, arquivo: file, preview: URL.createObjectURL(file), nomeOriginal: file.name }); e.target.value = ''; };
  const insertShortcode = (code) => {
    const ta = textRef.current;
    if (!ta) { onUpdate({ ...step, conteudo: (step.conteudo || '') + code }); return; }
    const start = ta.selectionStart; const end = ta.selectionEnd;
    const text = step.conteudo || '';
    const newText = text.substring(0, start) + code + text.substring(end);
    onUpdate({ ...step, conteudo: newText });
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + code.length; }, 0);
  };
  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-white space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-lg">
          <Icon size={13} className="text-gray-600" />
          <span className="text-xs font-semibold text-gray-600">{index + 1}. {STEP_LABELS[step.tipo]}</span>
        </div>
        <div className="flex-1" />
        <button onClick={onMoveUp} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button onClick={onMoveDown} disabled={index === total - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
      </div>
      {step.tipo === 'texto' && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <ShortcodeSelector onInsert={insertShortcode} />
            <span className="text-xs text-gray-400">Use # para personalizar</span>
          </div>
          <textarea ref={textRef} value={step.conteudo || ''} onChange={e => onUpdate({ ...step, conteudo: e.target.value })} placeholder="Digite a mensagem..." rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y" />
        </div>
      )}
      {step.tipo === 'imagem' && (<div>
        {step.preview ? (<div className="flex items-center gap-3"><img src={step.preview} alt="" className="h-16 w-16 object-cover rounded-lg border" /><span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span><button onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>)
        : (<button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"><Image size={20} className="mx-auto mb-1 opacity-50" />Selecionar imagem</button>)}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>)}
      {step.tipo === 'video' && (<div>
        {step.preview ? (<div className="flex items-center gap-3"><video src={step.preview} className="h-16 w-24 object-cover rounded-lg border" /><span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span><button onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>)
        : (<button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"><Video size={20} className="mx-auto mb-1 opacity-50" />Selecionar vídeo</button>)}
        <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/*" className="hidden" onChange={handleFile} />
      </div>)}
      {step.tipo === 'audio' && (<div>
        {step.preview ? (<div className="flex items-center gap-3"><audio src={step.preview} controls className="h-8 flex-1" /><span className="text-xs text-gray-500 truncate max-w-[120px]">{step.nomeOriginal}</span><button onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>)
        : (<div className="flex items-center gap-2"><button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"><Upload size={13} /> Arquivo</button><AudioRecorder onAudioReady={({ blob, url, nome }) => onUpdate({ ...step, arquivo: blob, preview: url, nomeOriginal: nome })} /></div>)}
        <input ref={fileRef} type="file" accept=".mp3,.ogg,.opus,.wav,.m4a,.webm" className="hidden" onChange={handleFile} />
      </div>)}
      {step.tipo === 'documento' && (<div>
        {step.preview ? (<div className="flex items-center gap-3"><FileText size={20} className="text-gray-400" /><span className="text-xs text-gray-600 truncate flex-1">{step.nomeOriginal}</span><button onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>)
        : (<button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"><FileText size={20} className="mx-auto mb-1 opacity-50" />Selecionar documento</button>)}
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" className="hidden" onChange={handleFile} />
      </div>)}
    </div>
  );
}

export default function Agendamentos() {
  const qc = useQueryClient();
  const [nomeContato, setNomeContato] = useState('');
  const [telefone, setTelefone] = useState('');
  const [instancia, setInstancia] = useState('');
  const [agendarPara, setAgendarPara] = useState('');
  const [observacao, setObservacao] = useState('');
  const [steps, setSteps] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: instanciasData } = useQuery({ queryKey: ['evolution-instancias-ag'], queryFn: () => api.get('/envios-massa/instancias').then(r => r.data), refetchOnWindowFocus: false });
  const { data: agendamentosData, refetch } = useQuery({ queryKey: ['agendamentos'], queryFn: () => api.get('/agendamentos').then(r => r.data), refetchInterval: 10000 });

  const instancias = instanciasData?.data || [];
  const agendamentos = agendamentosData?.data || [];

  useEffect(() => { if (instancias.length && !instancia) setInstancia(instancias[0].name); }, [instancias]);

  const addStep = (tipo) => setSteps(prev => [...prev, { tipo, conteudo: '', arquivo: null, preview: null, nomeOriginal: '', legenda: '' }]);
  const updateStep = (idx, step) => setSteps(prev => prev.map((s, i) => i === idx ? step : s));
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));
  const moveStep = (idx, dir) => { const arr = [...steps]; const t = idx + dir; if (t < 0 || t >= arr.length) return; [arr[idx], arr[t]] = [arr[t], arr[idx]]; setSteps(arr); };

  const handleSubmit = async () => {
    setError('');
    if (!telefone.trim()) return setError('Informe o telefone do contato');
    if (!instancia) return setError('Selecione uma instância WhatsApp');
    if (!agendarPara) return setError('Informe a data e hora do agendamento');
    if (steps.length === 0) return setError('Adicione ao menos um bloco na esteira');
    if (new Date(agendarPara) <= new Date()) return setError('A data do agendamento deve ser no futuro');

    setSending(true);
    try {
      const fd = new FormData();
      const stepsConfig = steps.map(s => s.tipo === 'texto' ? { tipo: 'texto', conteudo: s.conteudo } : { tipo: s.tipo, legenda: s.legenda || '', nomeOriginal: s.nomeOriginal || '' });
      fd.append('config', JSON.stringify({
        nome_contato: nomeContato.trim(),
        telefone: telefone.trim(),
        instancia_evolution: instancia,
        agendar_para: agendarPara,
        observacao: observacao.trim(),
        steps: stepsConfig,
      }));
      steps.forEach((s, i) => { if (s.tipo !== 'texto' && s.arquivo) fd.append(`step_${i}`, s.arquivo, s.nomeOriginal || `step_${i}`); });
      await api.post('/agendamentos', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
      setSuccess('Mensagem agendada com sucesso!');
      setTimeout(() => setSuccess(''), 4000);
      qc.invalidateQueries(['agendamentos']);
      setNomeContato(''); setTelefone(''); setAgendarPara(''); setObservacao(''); setSteps([]);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setSending(false); }
  };

  const cancelar = async (id) => {
    try { await api.post(`/agendamentos/${id}/cancelar`); qc.invalidateQueries(['agendamentos']); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este agendamento?')) return;
    try { await api.delete(`/agendamentos/${id}`); qc.invalidateQueries(['agendamentos']); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Agendamento de Mensagens</h1>
          <p className="text-sm text-gray-500 mt-0.5">Programe envios automáticos para data e hora específicas</p>
        </div>
        <button onClick={() => refetch()} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50"><RefreshCw size={14} /> Atualizar</button>
      </div>

      {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl"><AlertCircle size={15} /> {error}<button onClick={() => setError('')} className="ml-auto"><X size={14} /></button></div>}
      {success && <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl"><CheckCircle2 size={15} /> {success}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Esquerda: Formulário */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow p-5 space-y-4">
            <h2 className="font-semibold text-gray-700 flex items-center gap-2 text-sm"><CalendarClock size={15} /> Novo Agendamento</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome do Contato</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={nomeContato} onChange={e => setNomeContato(e.target.value)} placeholder="Ex: João Silva" className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Telefone *</label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="44999998888" className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Data e Hora *</label>
                <input type="datetime-local" value={agendarPara} onChange={e => setAgendarPara(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Instância WhatsApp *</label>
                {instancias.length === 0
                  ? <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">Nenhuma instância conectada.</div>
                  : <select value={instancia} onChange={e => setInstancia(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">{instancias.map(i => <option key={i.name} value={i.name}>{i.name} — {i.profileName || ''}</option>)}</select>}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Observação (opcional)</label>
              <input value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Ex: Cliente pediu retorno segunda às 8h" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>

          {/* Esteira */}
          <div className="bg-white rounded-xl shadow p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-700 flex items-center gap-2 text-sm"><Settings2 size={15} /> Esteira de Mensagens</h2>
              <span className="text-xs text-gray-400">{steps.length} bloco(s)</span>
            </div>
            <p className="text-xs text-gray-400">Monte a sequência de mensagens que será enviada no horário agendado.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => addStep('texto')} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100"><Type size={13} /> + Texto</button>
              <button onClick={() => addStep('imagem')} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100"><Image size={13} /> + Imagem</button>
              <button onClick={() => addStep('video')} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100"><Video size={13} /> + Vídeo</button>
              <button onClick={() => addStep('audio')} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium hover:bg-orange-100"><Music size={13} /> + Áudio</button>
              <button onClick={() => addStep('documento')} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200"><FileText size={13} /> + Documento</button>
            </div>
            {steps.length > 0 && (
              <div className="space-y-2 mt-2">
                {steps.map((step, i) => <StepCard key={i} step={step} index={i} total={steps.length} onUpdate={s => updateStep(i, s)} onRemove={() => removeStep(i)} onMoveUp={() => moveStep(i, -1)} onMoveDown={() => moveStep(i, 1)} />)}
              </div>
            )}
            {steps.length === 0 && <div className="py-6 text-center text-gray-300 text-sm">Nenhum bloco adicionado.</div>}
          </div>

          <button onClick={handleSubmit} disabled={sending} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 shadow">
            <CalendarClock size={16} /> {sending ? 'Agendando...' : 'Agendar Envio'}
          </button>
        </div>

        {/* Direita: Lista de agendamentos */}
        <div className="bg-white rounded-xl shadow p-5 space-y-4 flex flex-col">
          <h2 className="font-semibold text-gray-700 flex items-center gap-2 text-sm"><Clock size={15} /> Agendamentos</h2>
          {agendamentos.length === 0 && <div className="py-10 text-center text-gray-300 text-sm">Nenhum agendamento criado ainda.</div>}
          <div className="space-y-2 overflow-y-auto max-h-[600px]">
            {agendamentos.map(ag => (
              <div key={ag.id} className="border border-gray-100 rounded-xl p-3 hover:bg-gray-50 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">{ag.nome_contato || ag.telefone}</span>
                    <StatusBadge status={ag.status} />
                  </div>
                  <div className="flex items-center gap-1">
                    {ag.status === 'agendada' && (
                      <button onClick={() => cancelar(ag.id)} className="text-xs text-yellow-600 hover:text-yellow-800 px-2 py-1 rounded hover:bg-yellow-50">Cancelar</button>
                    )}
                    <button onClick={() => excluir(ag.id)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Phone size={11} /> {ag.telefone}</span>
                  <span className="flex items-center gap-1"><CalendarClock size={11} /> {fmtDate(ag.agendar_para)}</span>
                </div>
                {ag.observacao && <p className="text-xs text-gray-400 italic">{ag.observacao}</p>}
                {ag.status === 'erro' && ag.erro_msg && <p className="text-xs text-red-500">{ag.erro_msg}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
